<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref} from 'vue'
import Container from '@/components/www/primitives/Container.vue'
import OselloAnimatedMark from '@/components/www/primitives/OselloAnimatedMark.vue'
import G2RatingBadge from '@/components/www/g2/G2RatingBadge.vue'
import {appUrlTo} from '@/lib/urlTo'
import {useCookieBanner} from '@/composables/www/useCookieBanner'

/**
 * Site footer (ported from osello.com) — deep brand-green block with the
 * "Marketplace, automated." crown tagline (per-letter reveal on scroll),
 * utility columns, and a muted legal row. Links point at real www routes.
 */
const crownSegments = [
    {text: 'Marketplace, ', accent: false},
    {text: 'automated.', accent: true},
]

interface FooterLink {
    label: string
    to?: string
    href?: string
    external?: boolean
}

const footerColumns: {title: string, links: FooterLink[]}[] = [
    {
        title: 'Product',
        links: [
            {label: 'Features', to: '/features'},
            {label: 'How It Works', to: '/how-it-works'},
            {label: 'Pricing', to: '/pricing'},
            {label: 'Marketplaces', to: '/marketplaces'},
            {label: 'Integrations', to: '/integrations'},
        ],
    },
    {
        title: 'Company',
        links: [
            {label: 'About Us', to: '/about'},
            {label: 'Partners', to: '/partners'},
            {label: 'Contact Us', to: '/contact'},
            {label: 'Privacy Policy', to: '/legal/privacy-policy'},
            {label: 'Terms of Service', to: '/legal/terms-of-service'},
            {label: 'LinkedIn', href: 'https://www.linkedin.com/company/osello/', external: true},
        ],
    },
    // Order follows the Resources content model: editorial first (Reports, Case Studies,
    // Blog), then docs and updates. More resource categories join as they ship.
    {
        title: 'Resources',
        links: [
            {label: 'Reports', to: '/reports'},
            {label: 'Tools', to: '/tools'},
            {label: 'Case Studies', to: '/case-studies'},
            {label: 'Blog', to: '/blog'},
            {label: 'Help Center', to: '/help'},
            {label: 'Announcements', to: '/announcements'},
            {label: 'Changelog', to: '/change-logs'},
            {label: 'API Docs', to: '/api-docs'},
            {label: 'MCP Server', to: '/mcp'},
            {label: 'Login', href: appUrlTo('login')},
        ],
    },
]

const {open: openCookieBanner} = useCookieBanner()

// Per-letter crown reveal — stagger applied via inline transition delay.
const STAGGER_MS = 50
const footerEl = ref<HTMLElement | null>(null)
const crownEl = ref<HTMLElement | null>(null)
const revealed = ref(false)
let observer: IntersectionObserver | null = null

function letterDelay(segmentIndex: number, charIndex: number): string {
    const priorChars = crownSegments
        .slice(0, segmentIndex)
        .reduce((sum, segment) => sum + segment.text.length, 0)
    return `${(priorChars + charIndex) * STAGGER_MS}ms`
}

/**
 * Paint one continuous gradient across the accent word by aligning each
 * letter's text-clipped gradient to the whole word's box. Proportional-font
 * letter widths can't be known in CSS, so the offsets are measured here.
 */
function paintAccentGradient() {
    const wordEl = crownEl.value?.querySelector<HTMLElement>('.footer-crown__word--accent')
    if (!wordEl) return
    const wordRect = wordEl.getBoundingClientRect()
    wordEl.querySelectorAll<HTMLElement>('.footer-crown__letter').forEach((letter) => {
        letter.style.backgroundSize = `${wordRect.width}px 100%`
        letter.style.backgroundPositionX = `${wordRect.left - letter.getBoundingClientRect().left}px`
    })
}

onMounted(() => {
    paintAccentGradient()
    // Web font may change letter widths after load; realign once it's ready.
    document.fonts?.ready.then(paintAccentGradient).catch(() => {})
    window.addEventListener('resize', paintAccentGradient)

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        revealed.value = true
        return
    }
    observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    revealed.value = true
                    observer?.disconnect()
                }
            }
        },
        {threshold: 0.1, rootMargin: '0px 0px -48px 0px'},
    )
    if (footerEl.value) observer.observe(footerEl.value)
})

onBeforeUnmount(() => {
    observer?.disconnect()
    window.removeEventListener('resize', paintAccentGradient)
})
</script>

<template>
  <footer ref="footerEl" class="site-footer" :class="{'is-revealed': revealed}">
    <div class="site-footer__inner">
      <Container width="lg">
        <!-- Type moment — per-letter reveal on scroll. -->
        <div class="footer-crown">
          <p
              ref="crownEl"
              class="footer-crown__line"
              :class="{'is-revealed': revealed}"
              data-footer-reveal
              aria-label="Marketplace, automated."
          >
            <span
                v-for="(segment, segmentIndex) in crownSegments"
                :key="segmentIndex"
                class="footer-crown__word"
                :class="{'footer-crown__word--accent': segment.accent}"
            >
              <span
                  v-for="(char, charIndex) in [...segment.text]"
                  :key="charIndex"
                  class="footer-crown__letter"
                  :class="{'footer-crown__letter--accent': segment.accent}"
                  :style="{transitionDelay: letterDelay(segmentIndex, charIndex)}"
                  aria-hidden="true"
              >{{ char === ' ' ? '\u00A0' : char }}</span>
            </span>
          </p>
        </div>

        <div class="footer-cols">
          <div class="footer-brand">
            <RouterLink class="footer-icon" to="/" aria-label="Osello home">
              <OselloAnimatedMark class="footer-icon__mark" animate-on-hover/>
            </RouterLink>
            <p class="footer-brand__tagline">
              Osello. Marketplace automation for fashion brands.
            </p>
            <a class="footer-brand__contact" href="mailto:hello@osello.com">
              Talk to a human
            </a>
            <G2RatingBadge class="footer-g2" location="footer" theme="dark" showLogo/>
          </div>

          <nav class="footer-nav" aria-label="Footer">
            <div v-for="col in footerColumns" :key="col.title" class="footer-col">
              <h2 class="footer-col__title">{{ col.title }}</h2>
              <ul class="footer-col__list">
                <li v-for="link in col.links" :key="link.label">
                  <RouterLink v-if="link.to" class="footer-link" :to="link.to">
                    {{ link.label }}
                  </RouterLink>
                  <a
                      v-else
                      class="footer-link"
                      :href="link.href"
                      :data-cta="link.label === 'Login' ? 'login' : undefined"
                      data-cta-location="footer"
                      :target="link.external ? '_blank' : undefined"
                      :rel="link.external ? 'noopener noreferrer' : undefined"
                  >
                    {{ link.label }}
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        <div class="footer-legal">
          <p class="footer-legal__copy">&copy; 2026 Osello Corp. All rights reserved.</p>
          <button
              type="button"
              class="footer-legal__link"
              data-cookie-preferences
              @click="openCookieBanner"
          >
            Cookie preferences
          </button>
        </div>
      </Container>
    </div>
  </footer>
</template>

<style scoped>
.site-footer {
    background-color: var(--secondary-700);
    color: var(--text-white);
    padding-block: var(--space-14) var(--space-10);
}

/* --- Type moment (the big crown) --------------------------------------- */
.footer-crown {
    padding-block: var(--space-8) var(--space-14);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-crown__line {
    font-family: var(--font-heading);
    font-weight: var(--fw-medium);
    font-size: clamp(64px, 12vw, 200px);
    line-height: 1;
    letter-spacing: -0.03em;
    color: var(--text-white);
    margin: 0;
}

.footer-crown__word {
    display: inline-block;
    white-space: nowrap;
}

.footer-crown__letter {
    display: inline-block;
    transition:
        opacity 520ms var(--ease-out-strong),
        transform 520ms var(--ease-out-strong);
}

/* Gradient text-clip must live on the letters, not the word: the per-letter
   inline-block spans paint their own glyphs, so a clip on the parent word has
   no text of its own to reveal and renders fully transparent (invisible).
   `paintAccentGradient()` sizes/positions each letter's gradient to the word
   box so they read as one continuous sweep; the values here are the graceful
   fallback (per-letter gradient) if that script hasn't run yet. */
.footer-crown__letter--accent {
    background: linear-gradient(
        105deg,
        var(--primary-400) 0%,
        var(--primary-500) 52%,
        var(--primary-600) 100%
    );
    background-repeat: no-repeat;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

/* Hidden until IO fires — stagger applied via inline delay. */
[data-footer-reveal]:not(.is-revealed) .footer-crown__letter {
    opacity: 0;
    transform: translateY(0.45em);
}

[data-footer-reveal].is-revealed .footer-crown__letter {
    opacity: 1;
    transform: translateY(0);
}

/* --- Columns ----------------------------------------------------------- */
.footer-cols {
    display: grid;
    grid-template-columns: 1.4fr 3fr;
    gap: var(--space-12);
    padding-block: var(--space-14);
    opacity: 0;
    transform: translateY(12px);
}

.site-footer.is-revealed .footer-cols {
    opacity: 1;
    transform: translateY(0);
    transition:
        opacity 480ms var(--ease-out-strong) 360ms,
        transform 480ms var(--ease-out-strong) 360ms;
}

.footer-brand {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-5);
}

/* O-mark icon — replaces the wordmark in the brand column.
   The size lives on this link, not on the mark: OselloAnimatedMark sets
   width/height 100% on its own root, which ties on specificity with any sizing
   rule targeting that same element and resolves by stylesheet order. Sizing the
   parent instead makes the result the same whichever chunk Vite emits first. */
.footer-icon {
    display: inline-flex;
    align-items: center;
    width: 48px;
    height: 48px;
    color: var(--accent-500);
    line-height: 0;
    transition: transform 160ms var(--ease-out-strong);
}

@media (hover: hover) and (pointer: fine) {
    .footer-icon:active {
        transform: scale(0.96);
    }
}

.footer-brand__tagline {
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.6);
    max-width: 28ch;
}

/* The G2 badge as a card: hairline border and a faint lift of the brand green. */
.footer-g2 {
    margin-top: 15px;
    padding: 10px 16px 10px 10px;
    border: 1px solid rgba(111, 175, 156, 0.28);
    border-radius: 14px;
    background: rgba(111, 175, 156, 0.08);
    transition:
            border-color 200ms var(--ease-out),
            background 200ms var(--ease-out),
            transform 200ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .footer-g2:hover {
        border-color: rgba(111, 175, 156, 0.55);
        background: rgba(111, 175, 156, 0.14);
    }
}

@media (prefers-reduced-motion: reduce) {
    .footer-g2 {
        transition: none;
    }
}

.footer-brand__contact {
    font-family: var(--font-body);
    font-size: 17px;
    font-weight: var(--fw-medium);
    color: var(--accent-500);
    transition: color 200ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .footer-brand__contact:hover {
        color: var(--text-white);
    }
}

.footer-nav {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-9);
}

.footer-col__title {
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: var(--fw-semibold);
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--text-white);
    margin-bottom: var(--space-5);
}

.footer-col__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    margin: 0;
}

.footer-link {
    font-family: var(--font-body);
    font-size: 16px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.6);
    transition: color 200ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .footer-link:hover {
        color: var(--text-white);
    }
}

/* --- Legal ------------------------------------------------------------- */
.footer-legal {
    padding-top: var(--space-8);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    font-family: var(--font-body);
    font-size: 14px;
    opacity: 0;
    transform: translateY(8px);
}

.site-footer.is-revealed .footer-legal {
    opacity: 1;
    transform: translateY(0);
    transition:
        opacity 420ms var(--ease-out-strong) 480ms,
        transform 420ms var(--ease-out-strong) 480ms;
}

.footer-legal {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-4) var(--space-7);
}

.footer-legal__copy {
    margin: 0;
}

.footer-legal__link {
    padding: 0;
    background: none;
    border: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    transition: color 200ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .footer-legal__link:hover {
        color: var(--text-white);
    }
}

/* Focus rings on dark. */
.footer-link:focus-visible,
.footer-legal__link:focus-visible,
.footer-brand__contact:focus-visible,
.footer-icon:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 4px;
}

/* --- Responsive -------------------------------------------------------- */
@media (max-width: 900px) {
    .footer-cols {
        grid-template-columns: 1fr;
        gap: var(--space-10);
    }

    .footer-nav {
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-9) var(--space-8);
    }
}

/* Reduced motion: full tagline, no stagger. */
@media (prefers-reduced-motion: reduce) {
    .footer-crown__letter {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
    }

    .footer-cols,
    .footer-legal,
    .site-footer.is-revealed .footer-cols,
    .site-footer.is-revealed .footer-legal {
        opacity: 1;
        transform: none;
        transition: none;
    }

    .footer-icon {
        transition: none;
    }

    .footer-icon:active {
        transform: none;
    }
}
</style>
